import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { ordersAPI } from '../../services/api';
import './Cart.css';

declare global {
    interface Window {
        ym?: (id: number, action: string, goal: string) => void;
    }
}

const Cart: React.FC = () => {
    const { t } = useTranslation();
    const { items, removeItem, updateQuantity, clearCart, isCartOpen, setIsCartOpen, totalItems } = useCart();
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (items.length === 0) return;

        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const cartItemsText = items.map(item =>
                `${item.name} (x${item.quantity})`
            ).join(', ');

            const cartItemsJson = JSON.stringify(items.map(item => ({
                productId: item.productId,
                name: item.name,
                category: item.category,
                quantity: item.quantity
            })));

            const fullMessage = formData.message
                ? `${formData.message}\n\n---\nCart items: ${cartItemsText}`
                : `Cart items: ${cartItemsText}`;

            await ordersAPI.create({
                customer_name: formData.name,
                customer_email: formData.email,
                customer_phone: formData.phone,
                message: fullMessage,
                cart_items: cartItemsJson
            });

            if (window.ym) {
                window.ym(12345678, 'reachGoal', 'cart_submit');
            }

            setSubmitStatus('success');
            clearCart();
            setFormData({ name: '', email: '', phone: '', message: '' });
            setTimeout(() => {
                setShowForm(false);
                setSubmitStatus('idle');
                setIsCartOpen(false);
            }, 2000);
        } catch (error) {
            console.error('Error submitting cart:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isCartOpen) return null;

    return (
        <>
            <div className="cart-overlay" onClick={() => setIsCartOpen(false)} />
            <div className="cart-drawer">
                <div className="cart-header">
                    <h2>{t('cart.title', 'Cart')} ({totalItems})</h2>
                    <button className="cart-close-btn" onClick={() => setIsCartOpen(false)}>✕</button>
                </div>

                <div className="cart-body">
                    {items.length === 0 ? (
                        <div className="cart-empty">
                            <p>{t('cart.empty', 'Your cart is empty')}</p>
                        </div>
                    ) : (
                        <>
                            <div className="cart-items">
                                {items.map(item => (
                                    <div key={item.productId} className="cart-item">
                                        {item.image && (
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="cart-item-image"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/60x60?text=No+Img';
                                                }}
                                            />
                                        )}
                                        <div className="cart-item-info">
                                            <h4>{item.name}</h4>
                                            <span className="cart-item-category">{item.category}</span>
                                        </div>
                                        <div className="cart-item-quantity">
                                            <button onClick={() => updateQuantity(item.productId, item.quantity - 1)}>−</button>
                                            <span>{item.quantity}</span>
                                            <button onClick={() => updateQuantity(item.productId, item.quantity + 1)}>+</button>
                                        </div>
                                        <button
                                            className="cart-item-remove"
                                            onClick={() => removeItem(item.productId)}
                                            title={t('cart.remove', 'Remove')}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {!showForm ? (
                                <button className="cart-submit-btn" onClick={() => setShowForm(true)}>
                                    {t('cart.sendInquiry', 'Send Inquiry')}
                                </button>
                            ) : (
                                <form className="cart-form" onSubmit={handleSubmit}>
                                    <div className="cart-form-group">
                                        <input
                                            type="text"
                                            placeholder={t('contact.form.name', 'Name') + ' *'}
                                            value={formData.name}
                                            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="cart-form-group">
                                        <input
                                            type="email"
                                            placeholder={t('contact.form.email', 'Email') + ' *'}
                                            value={formData.email}
                                            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="cart-form-group">
                                        <input
                                            type="text"
                                            placeholder={t('contact.form.company', 'Phone') + ' *'}
                                            value={formData.phone}
                                            onChange={e => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="cart-form-group">
                                        <textarea
                                            placeholder={t('contact.form.message', 'Message')}
                                            value={formData.message}
                                            onChange={e => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                            rows={3}
                                        />
                                    </div>
                                    <div className="cart-form-actions">
                                        <button type="button" className="cart-form-cancel" onClick={() => setShowForm(false)}>
                                            {t('cart.cancel', 'Cancel')}
                                        </button>
                                        <button type="submit" className="cart-form-submit" disabled={isSubmitting}>
                                            {isSubmitting
                                                ? t('contact.form.sending', 'Sending...')
                                                : t('cart.send', 'Send')}
                                        </button>
                                    </div>

                                    {submitStatus === 'success' && (
                                        <div className="cart-alert cart-alert-success">
                                            {t('contact.form.success', 'Message sent successfully!')}
                                        </div>
                                    )}
                                    {submitStatus === 'error' && (
                                        <div className="cart-alert cart-alert-error">
                                            {t('contact.form.error', 'Failed to send. Please try again.')}
                                        </div>
                                    )}
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default Cart;
